import { Role } from "../auth/types/authType";
import { RoleTypes } from "../auth/types/roleTypes";
import {
  NotificationAudience,
  NotificationMessageTypes,
} from "../process/types/processTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";

/** Hidden from navbar popup, badge count, and browser push. */
export const POPUP_EXCLUDED_MESSAGES: string[] = [
  NotificationMessageTypes.updated,
  NotificationMessageTypes.first_timer_added,
];

export const EXPIRY_ALERT_MESSAGES: string[] = [
  NotificationMessageTypes.medical_expiring,
  NotificationMessageTypes.passport_expiring,
  NotificationMessageTypes.labour_expiring,
  NotificationMessageTypes.insurance_expiring,
  NotificationMessageTypes.selection_due,
];

export function isExpiryAlertMessage(message: string): boolean {
  return EXPIRY_ALERT_MESSAGES.includes(message);
}

export function notificationActionLabel(
  message: string,
  contextText: string | undefined,
  translate: (key: string) => string,
): string {
  const label = translate(message);
  if (isExpiryAlertMessage(message) && contextText) {
    return `${label} — ${contextText}`;
  }
  return label;
}

export function shouldShowInPopup(message: string): boolean {
  return !POPUP_EXCLUDED_MESSAGES.includes(message);
}

const MODULE_TO_AUDIENCE: Record<string, NotificationAudience> = {
  Records: NotificationAudience.records,
  ProcessControl: NotificationAudience.pipeline,
  Wall: NotificationAudience.wall,
};

const NOTIFICATIONS_PATH = "/app/notifications";

function moduleOf(displayName: string): string {
  return displayName.includes(".")
    ? displayName.slice(0, displayName.indexOf("."))
    : displayName;
}

function employeeIdOf(
  employee?: { _id?: string } | string | null,
): string | undefined {
  if (!employee) return undefined;
  if (typeof employee === "string") {
    const id = employee.slice(0, 24);
    return id || undefined;
  }
  return employee._id || undefined;
}

/** Audiences the current user may view (empty = none). */
export function getUserNotificationAudiences(
  roles: Role[],
): NotificationAudience[] {
  if (isPrivilegedTransaction(roles, RoleTypes.ManageUsers)) {
    const audiences = [
      NotificationAudience.records,
      NotificationAudience.pipeline,
    ];
    if (isPrivilegedTransaction(roles, RoleTypes.ViewWall)) {
      audiences.push(NotificationAudience.wall);
    }
    return audiences;
  }

  const audiences = new Set<NotificationAudience>();
  for (const role of roles) {
    const name = role.displayName ?? "";
    const aud = MODULE_TO_AUDIENCE[moduleOf(name)];
    if (aud) audiences.add(aud);
  }
  return Array.from(audiences);
}

export function hasNotificationAudience(
  roles: Role[],
  audience: NotificationAudience,
): boolean {
  return getUserNotificationAudiences(roles).includes(audience);
}

function wallPostIdOf(notification: {
  wallPostRef?: string;
  wallPost?: { _id?: string } | string | null;
}): string | undefined {
  if (notification.wallPostRef) {
    return String(notification.wallPostRef);
  }
  const wallPost = notification.wallPost;
  if (!wallPost) return undefined;
  if (typeof wallPost === "string") {
    return wallPost.slice(0, 24) || undefined;
  }
  const id = wallPost._id;
  return id ? String(id) : undefined;
}

/** Navbar popup: wall notifications always deep-link to the wall. */
export function wallNotificationLink(notification: {
  wallPostRef?: string;
  wallPost?: { _id?: string } | string | null;
}): string {
  const wallPostId = wallPostIdOf(notification);
  return wallPostId ? `/app/wall?postId=${wallPostId}` : "/app/wall";
}

export function notificationLink(notification: {
  wallPostRef?: string;
  wallPost?: { _id?: string } | string | null;
  employee?: { _id?: string } | string | null;
  audience?: NotificationAudience;
}): string {
  const wallPostId = wallPostIdOf(notification);
  if (wallPostId) {
    return `/app/wall?postId=${wallPostId}`;
  }

  if (notification.audience === NotificationAudience.wall) {
    return "/app/wall";
  }

  const employeeId = employeeIdOf(notification.employee);
  if (employeeId) {
    return `/app/employee-detail/${employeeId}`;
  }

  return NOTIFICATIONS_PATH;
}

export { NotificationAudience };
